/**
 * UWDebtScheduleTab.jsx
 * Excel-replica of the "Debt Schedule (Borrower)" tab.
 * Existing Debts table + Proposed New Debt section.
 * Auto-calculates subtotals, totals, annual/monthly payments,
 * and effective rate for variable debts.
 */
import React, { useState, useCallback, useMemo } from "react";
import { cn, formatCurrency, formatPercent } from "../../lib/utils";
import { Button } from "../ui/button";
import { PlusCircle, Trash2, ChevronDown } from "lucide-react";

const DEBT_TYPES = ["Fixed", "Variable"];
const LENDER_PLACEHOLDER = "Lender / Description";

const EMPTY_EXISTING = {
  id: crypto.randomUUID(),
  lender: "",
  originalDate: "",
  originalAmount: 0,
  terms: 0,
  pricing: "Fixed",
  fixedRate: 0,
  baseRate: 0,
  margin: 0,
  floor: 0,
  currentBalance: 0,
  monthlyPayment: 0,
  monthlyOverride: 0,
  notes: "",
};

const EMPTY_PROPOSED = {
  id: crypto.randomUUID(),
  lender: "",
  date: "Proposed",
  amount: 0,
  terms: 120,
  pricing: "Variable",
  fixedRate: 0,
  baseRate: 7.0,
  margin: 2.75,
  floor: 0,
  currentBalance: 0,
  monthlyPayment: 0,
};

function calcEffectiveRate(pricing, fixedRate, baseRate, margin, floor) {
  if (pricing === "Fixed") return Number(fixedRate) || 0;
  const rate = (Number(baseRate) || 0) + (Number(margin) || 0);
  return Math.max(rate, Number(floor) || 0);
}

function calcAnnualPayment(monthly, override) {
  const m = Number(override) || Number(monthly) || 0;
  return m * 12;
}

function calcStressPayment(monthly, override, stressBump = 2.0) {
  // Simple approximation: rate increases, payment increases proportionally
  const m = Number(override) || Number(monthly) || 0;
  return m * 12 * (1 + stressBump / 100);
}

// ── Editable cell types ────────────────────────────────────────────────────

function TextCell({ value, onChange, placeholder = "" }) {
  return (
    <td className="px-1 py-0.5 border-r border-gray-200">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full min-w-[120px] text-xs bg-blue-50 border border-blue-100 rounded px-2 py-0.5
                   focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white"
      />
    </td>
  );
}

function NumCell({ value, onChange, prefix = "", small = false, readOnly = false }) {
  const num = Number(value) || 0;
  if (readOnly) {
    return (
      <td className={cn("px-2 py-0.5 text-right font-mono tabular-nums border-r border-gray-200",
        small ? "text-[11px]" : "text-xs",
        num < 0 ? "text-red-600" : num !== 0 ? "text-gray-800" : "text-gray-300"
      )}>
        {num !== 0 ? (prefix === "$" ? formatCurrency(num) : prefix ? `${num}${prefix}` : String(num)) : ""}
      </td>
    );
  }
  return (
    <td className="px-1 py-0.5 border-r border-gray-200">
      <input
        type="number"
        value={value === 0 ? "" : value}
        onChange={e => onChange(Number(e.target.value) || 0)}
        placeholder="0"
        className={cn("text-right font-mono tabular-nums bg-blue-50 border border-blue-100 rounded px-2 py-0.5",
          "focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white",
          small ? "text-[11px] w-16" : "text-xs w-24"
        )}
      />
    </td>
  );
}

function SelectCell({ value, onChange, options }) {
  return (
    <td className="px-1 py-0.5 border-r border-gray-200">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="text-xs bg-blue-50 border border-blue-100 rounded px-1 py-0.5 w-20
                   focus:outline-none focus:ring-1 focus:ring-blue-400"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </td>
  );
}

function TotalCell({ value, bold = false }) {
  const num = Number(value) || 0;
  return (
    <td className={cn(
      "px-2 py-1 text-right font-mono text-xs tabular-nums border-r border-gray-300",
      bold ? "font-bold" : "font-semibold",
      num < 0 ? "text-red-700" : num !== 0 ? "text-[#1e3a5f]" : "text-gray-300"
    )}>
      {num !== 0 ? formatCurrency(num) : ""}
    </td>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function UWDebtScheduleTab({ initialData, borrowerName = "0", onDataChange }) {
  const [existing, setExisting] = useState(
    initialData?.existing?.length ? initialData.existing : [{ ...EMPTY_EXISTING, id: crypto.randomUUID() }]
  );
  const [proposed, setProposed] = useState(
    initialData?.proposed?.length ? initialData.proposed : [{ ...EMPTY_PROPOSED, id: crypto.randomUUID() }]
  );
  const [comments, setComments] = useState(initialData?.comments || "");

  const updateExisting = useCallback((id, key, value) => {
    setExisting(prev => {
      const next = prev.map(d => d.id === id ? { ...d, [key]: value } : d);
      onDataChange?.({ existing: next, proposed });
      return next;
    });
  }, [proposed, onDataChange]);

  const updateProposed = useCallback((id, key, value) => {
    setProposed(prev => {
      const next = prev.map(d => d.id === id ? { ...d, [key]: value } : d);
      onDataChange?.({ existing, proposed: next });
      return next;
    });
  }, [existing, onDataChange]);

  const addExisting = () => setExisting(prev => [...prev, { ...EMPTY_EXISTING, id: crypto.randomUUID() }]);
  const removeExisting = (id) => setExisting(prev => prev.filter(d => d.id !== id));
  const addProposed = () => setProposed(prev => [...prev, { ...EMPTY_PROPOSED, id: crypto.randomUUID() }]);
  const removeProposed = (id) => setProposed(prev => prev.filter(d => d.id !== id));

  // Derived totals
  const existingTotals = useMemo(() => {
    const origAmt    = existing.reduce((s, d) => s + (Number(d.originalAmount) || 0), 0);
    const balance    = existing.reduce((s, d) => s + (Number(d.currentBalance) || 0), 0);
    const monthly    = existing.reduce((s, d) => s + (Number(d.monthlyOverride) || Number(d.monthlyPayment) || 0), 0);
    const annual     = monthly * 12;
    return { origAmt, balance, monthly, annual, stressAnnual: annual * 1.02 };
  }, [existing]);

  const proposedTotals = useMemo(() => {
    const origAmt = proposed.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const balance = proposed.reduce((s, d) => s + (Number(d.currentBalance) || 0), 0);
    const monthly = proposed.reduce((s, d) => s + (Number(d.monthlyPayment) || 0), 0);
    const annual  = monthly * 12;
    return { origAmt, balance, monthly, annual };
  }, [proposed]);

  const grandTotals = {
    origAmt: existingTotals.origAmt + proposedTotals.origAmt,
    balance: existingTotals.balance + proposedTotals.balance,
    monthly: existingTotals.monthly + proposedTotals.monthly,
    annual:  existingTotals.annual  + proposedTotals.annual,
  };

  const COL_CLASSES = "bg-[#1e3a5f] text-white text-[10px] font-semibold px-2 py-1.5 text-center border-r border-gray-600 whitespace-nowrap";

  return (
    <div className="h-full overflow-auto bg-white">
      {/* Borrower bar */}
      <div className="flex items-center gap-4 px-4 py-2 bg-[#dce6f1] border-b border-gray-300 sticky top-0 z-20">
        <span className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">Borrower:</span>
        <span className="text-sm font-mono text-[#1e3a5f] font-semibold">{borrowerName}</span>
        <span className="ml-auto text-xs text-amber-600 font-medium flex items-center gap-1">
          ⚠ Inserting additional debt lines will cause errors to cash flow — review after editing
        </span>
      </div>

      <div className="px-4 py-2 border-b border-gray-200">
        <h3 className="text-sm font-bold text-[#1e3a5f] uppercase tracking-wide">Debt Schedule (Borrower)</h3>
      </div>

      <div className="p-4 overflow-x-auto">
        {/* ── Existing Debts Table ── */}
        <table className="border-collapse w-full text-xs mb-1">
          <thead>
            <tr>
              <th className={COL_CLASSES} style={{ minWidth: 160 }}>Lender / Description</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>Original Date</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>Original Amount</th>
              <th className={COL_CLASSES} style={{ minWidth: 70 }}>Terms (Months)</th>
              <th className={COL_CLASSES} style={{ minWidth: 80 }}>Pricing: Fixed / Variable</th>
              <th className={COL_CLASSES} style={{ minWidth: 70 }}>Fixed Rate %</th>
              <th colSpan={3} className={cn(COL_CLASSES, "bg-[#2d5fa8]")}>For Variable Rates Only</th>
              <th className={COL_CLASSES} style={{ minWidth: 90 }}>Effective Rate</th>
              <th className={COL_CLASSES} style={{ minWidth: 90 }}>+2.00% Stress Rate</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>Current Balance</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>Monthly Payment</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>Annual Payment</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>+2.0% Rate Shock Annual</th>
              <th className={COL_CLASSES} style={{ minWidth: 100 }}>Monthly Payment Override</th>
              <th className={cn(COL_CLASSES, "bg-[#833b00]")} style={{ minWidth: 40 }}></th>
            </tr>
            <tr className="bg-[#2d5fa8]">
              {/* sub-headers for variable cols */}
              {[...Array(6)].map((_, i) => <th key={i} className="border-r border-gray-600 py-1" />)}
              <th className="text-[10px] text-white font-semibold px-2 py-1 border-r border-gray-600">Base Rate %</th>
              <th className="text-[10px] text-white font-semibold px-2 py-1 border-r border-gray-600">Margin %</th>
              <th className="text-[10px] text-white font-semibold px-2 py-1 border-r border-gray-600">Floor</th>
              {[...Array(7)].map((_, i) => <th key={i} className="border-r border-gray-600 py-1" />)}
            </tr>
          </thead>
          <tbody>
            {existing.map((d) => {
              const eff   = calcEffectiveRate(d.pricing, d.fixedRate, d.baseRate, d.margin, d.floor);
              const stress = eff + 2.0;
              const annual = calcAnnualPayment(d.monthlyPayment, d.monthlyOverride);
              const stressAnnual = calcStressPayment(d.monthlyPayment, d.monthlyOverride);
              return (
                <tr key={d.id} className="border-b border-gray-100 hover:bg-blue-50/30">
                  <TextCell  value={d.lender}         onChange={v => updateExisting(d.id, "lender", v)}         placeholder="Lender name" />
                  <TextCell  value={d.originalDate}   onChange={v => updateExisting(d.id, "originalDate", v)}   placeholder="MM/DD/YYYY" />
                  <NumCell   value={d.originalAmount}  onChange={v => updateExisting(d.id, "originalAmount", v)}  prefix="$" />
                  <NumCell   value={d.terms}           onChange={v => updateExisting(d.id, "terms", v)}           small />
                  <SelectCell value={d.pricing}        onChange={v => updateExisting(d.id, "pricing", v)}         options={DEBT_TYPES} />
                  <NumCell   value={d.fixedRate}       onChange={v => updateExisting(d.id, "fixedRate", v)}       prefix="%" small />
                  <NumCell   value={d.baseRate}        onChange={v => updateExisting(d.id, "baseRate", v)}        prefix="%" small />
                  <NumCell   value={d.margin}          onChange={v => updateExisting(d.id, "margin", v)}          prefix="%" small />
                  <NumCell   value={d.floor}           onChange={v => updateExisting(d.id, "floor", v)}           prefix="%" small />
                  {/* Derived read-only */}
                  <td className="px-2 py-0.5 text-right font-mono text-xs tabular-nums border-r border-gray-200 text-gray-700">
                    {eff ? formatPercent(eff) : ""}
                  </td>
                  <td className="px-2 py-0.5 text-right font-mono text-xs tabular-nums border-r border-gray-200 text-gray-700">
                    {stress ? formatPercent(stress) : ""}
                  </td>
                  <NumCell   value={d.currentBalance}  onChange={v => updateExisting(d.id, "currentBalance", v)}  prefix="$" />
                  <NumCell   value={d.monthlyPayment}   onChange={v => updateExisting(d.id, "monthlyPayment", v)}  prefix="$" />
                  {/* Annual & stress (derived) */}
                  <TotalCell value={annual} />
                  <TotalCell value={stressAnnual} />
                  <NumCell   value={d.monthlyOverride}  onChange={v => updateExisting(d.id, "monthlyOverride", v)} prefix="$" />
                  <td className="px-2 py-0.5 border-r border-gray-200">
                    <button onClick={() => removeExisting(d.id)} className="text-red-400 hover:text-red-600 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {/* Subtotals row */}
            <tr className="uw-subtotal-row">
              <td className="px-3 py-1.5 text-xs font-bold sticky left-0 bg-[#c6d9f1] text-[#1e3a5f] border-r border-gray-300">Subtotals</td>
              <td className="border-r border-gray-300"></td>
              <TotalCell value={existingTotals.origAmt} bold />
              {[...Array(8)].map((_, i) => <td key={i} className="border-r border-gray-300" />)}
              <TotalCell value={existingTotals.balance} bold />
              <TotalCell value={existingTotals.monthly} bold />
              <TotalCell value={existingTotals.annual}  bold />
              <TotalCell value={existingTotals.stressAnnual} bold />
              <td className="border-r border-gray-300"></td>
              <td></td>
            </tr>
          </tbody>
        </table>

        {/* Add row button */}
        <Button variant="outline" size="sm" onClick={addExisting} className="mb-4 text-xs gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50">
          <PlusCircle size={13} /> Add Existing Debt Line
        </Button>

        {/* ── Proposed New Debt Table ── */}
        <div className="mt-4">
          <div className="uw-row-header text-xs font-bold px-3 py-1.5 mb-1 uppercase tracking-wide">Proposed New Debt / Loan Being Financed</div>
          <table className="border-collapse w-full text-xs">
            <thead>
              <tr>
                <th className={COL_CLASSES} style={{ minWidth: 180 }}>Lender / Description</th>
                <th className={COL_CLASSES} style={{ minWidth: 80 }}>Date</th>
                <th className={COL_CLASSES} style={{ minWidth: 100 }}>Amount</th>
                <th className={COL_CLASSES} style={{ minWidth: 70 }}>Terms (Mo.)</th>
                <th className={COL_CLASSES} style={{ minWidth: 80 }}>Pricing</th>
                <th className={COL_CLASSES} style={{ minWidth: 70 }}>Fixed Rate %</th>
                <th className={cn(COL_CLASSES, "bg-[#2d5fa8]")} style={{ minWidth: 70 }}>Base Rate %</th>
                <th className={cn(COL_CLASSES, "bg-[#2d5fa8]")} style={{ minWidth: 70 }}>Margin %</th>
                <th className={cn(COL_CLASSES, "bg-[#2d5fa8]")} style={{ minWidth: 60 }}>Floor</th>
                <th className={COL_CLASSES} style={{ minWidth: 90 }}>Effective Rate</th>
                <th className={COL_CLASSES} style={{ minWidth: 90 }}>+2% Stress Rate</th>
                <th className={COL_CLASSES} style={{ minWidth: 100 }}>Current Balance</th>
                <th className={COL_CLASSES} style={{ minWidth: 100 }}>Monthly Payment</th>
                <th className={COL_CLASSES} style={{ minWidth: 100 }}>Annual Payment</th>
                <th className={cn(COL_CLASSES, "bg-[#833b00]")} style={{ minWidth: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {proposed.map((d) => {
                const eff    = calcEffectiveRate(d.pricing, d.fixedRate, d.baseRate, d.margin, d.floor);
                const stress = eff + 2.0;
                const annual = (Number(d.monthlyPayment) || 0) * 12;
                return (
                  <tr key={d.id} className="border-b border-gray-100 hover:bg-yellow-50/30">
                    <TextCell  value={d.lender}       onChange={v => updateProposed(d.id, "lender", v)} placeholder="New loan description" />
                    <TextCell  value={d.date}         onChange={v => updateProposed(d.id, "date", v)} />
                    <NumCell   value={d.amount}       onChange={v => updateProposed(d.id, "amount", v)}  prefix="$" />
                    <NumCell   value={d.terms}        onChange={v => updateProposed(d.id, "terms", v)}   small />
                    <SelectCell value={d.pricing}     onChange={v => updateProposed(d.id, "pricing", v)} options={DEBT_TYPES} />
                    <NumCell   value={d.fixedRate}    onChange={v => updateProposed(d.id, "fixedRate", v)} prefix="%" small />
                    <NumCell   value={d.baseRate}     onChange={v => updateProposed(d.id, "baseRate", v)} prefix="%" small />
                    <NumCell   value={d.margin}       onChange={v => updateProposed(d.id, "margin", v)}   prefix="%" small />
                    <NumCell   value={d.floor}        onChange={v => updateProposed(d.id, "floor", v)}    prefix="%" small />
                    <td className="px-2 py-0.5 text-right font-mono text-xs tabular-nums border-r border-gray-200 text-gray-700">
                      {eff ? formatPercent(eff) : ""}
                    </td>
                    <td className="px-2 py-0.5 text-right font-mono text-xs tabular-nums border-r border-gray-200 text-gray-700">
                      {stress ? formatPercent(stress) : ""}
                    </td>
                    <NumCell value={d.currentBalance} onChange={v => updateProposed(d.id, "currentBalance", v)} prefix="$" />
                    <NumCell value={d.monthlyPayment} onChange={v => updateProposed(d.id, "monthlyPayment", v)} prefix="$" />
                    <TotalCell value={annual} />
                    <td className="px-2 py-0.5">
                      <button onClick={() => removeProposed(d.id)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* Proposed subtotals */}
              <tr className="uw-subtotal-row">
                <td className="px-3 py-1.5 text-xs font-bold sticky left-0 bg-[#c6d9f1] text-[#1e3a5f] border-r border-gray-300">Subtotals</td>
                <td className="border-r border-gray-300"></td>
                <TotalCell value={proposedTotals.origAmt} bold />
                {[...Array(8)].map((_, i) => <td key={i} className="border-r border-gray-300" />)}
                <TotalCell value={proposedTotals.balance} bold />
                <TotalCell value={proposedTotals.monthly} bold />
                <TotalCell value={proposedTotals.annual}  bold />
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        <Button variant="outline" size="sm" onClick={addProposed} className="mt-2 mb-6 text-xs gap-1.5 text-green-700 border-green-300 hover:bg-green-50">
          <PlusCircle size={13} /> Add Proposed Debt Line
        </Button>

        {/* ── Grand Totals ── */}
        <table className="border-collapse text-xs">
          <tbody>
            <tr className="uw-total-row">
              <td className="px-4 py-2 font-bold min-w-[180px] bg-[#1e3a5f] text-white border-r border-gray-500">Totals</td>
              <td className="px-3 py-2 font-bold font-mono text-white border-r border-gray-500 min-w-[120px] text-right tabular-nums">
                {formatCurrency(grandTotals.origAmt)}
              </td>
              <td className="px-3 py-2 font-bold font-mono text-white border-r border-gray-500 min-w-[120px] text-right tabular-nums">
                {formatCurrency(grandTotals.balance)}
              </td>
              <td className="px-3 py-2 font-bold font-mono text-white border-r border-gray-500 min-w-[120px] text-right tabular-nums">
                {formatCurrency(grandTotals.monthly)}
              </td>
              <td className="px-3 py-2 font-bold font-mono text-white min-w-[120px] text-right tabular-nums">
                {formatCurrency(grandTotals.annual)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Comments ── */}
        <div className="mt-6">
          <label className="block text-xs font-bold text-[#1e3a5f] uppercase tracking-wide mb-1">Comments:</label>
          <textarea
            value={comments}
            onChange={e => { setComments(e.target.value); onDataChange?.({ existing, proposed, comments: e.target.value }); }}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            placeholder="Add underwriting notes about debt obligations..."
          />
        </div>
      </div>
    </div>
  );
}
